import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { fetchData, LOCALE, DEFAULT_LOCALE } from '../api/client';

const SiteContext = createContext(null);

/** Replace :name placeholders ("Our Services in :city"). */
export const fill = (text, params = {}) =>
    Object.entries(params).reduce((out, [key, value]) => out.replaceAll(`:${key}`, value ?? ''), text ?? '');

/**
 * Site-wide data from GET /site (brand, contact, menus, header & footer,
 * UI texts, cookie banner) and the language helpers. Everything renders
 * once this data is loaded.
 */
export function SiteProvider({ children }) {
    const [site, setSite] = useState(null);
    const [error, setError] = useState(null);
    const location = useLocation();

    useEffect(() => {
        fetchData('/site').then(setSite).catch(setError);
    }, []);

    useEffect(() => {
        document.documentElement.lang = LOCALE;
        document.documentElement.dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
        document.body.dir = document.documentElement.dir;
    }, []);

    // Marketing tags (written into the page by Laravel) count every page change of the app.
    useEffect(() => {
        window.darafTrack?.(window.location.pathname);
    }, [location.pathname]);

    /** A UI text: ui('common.learn_more') or ui('city.services_in', { city }). */
    const ui = useCallback(
        (key, params) => {
            const [group, name] = key.split('.');
            return fill(site?.ui?.[group]?.[name] ?? '', params);
        },
        [site],
    );

    /** The same page in the other language (a full load, so the page meta follow). */
    const otherLanguageUrl = useCallback(() => {
        const other = LOCALE === 'ar' ? 'en' : 'ar';
        const path = location.pathname === '/' ? '' : location.pathname;
        return `${other === DEFAULT_LOCALE ? '' : '/' + other}${path}${location.search}` || '/';
    }, [location]);

    const value = useMemo(
        () => ({
            site,
            locale: LOCALE,
            isRtl: LOCALE === 'ar',
            ui,
            otherLanguageUrl,
            switchLanguage: () => window.location.assign(otherLanguageUrl()),
        }),
        [site, ui, otherLanguageUrl],
    );

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}>
                {LOCALE === 'ar' ? 'تعذر تحميل الموقع. حاول مرة أخرى.' : 'The website could not be loaded. Please try again.'}
            </Box>
        );
    }

    if (!site) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: '#b8975a' }} />
            </Box>
        );
    }

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** { site, locale, isRtl, ui, otherLanguageUrl, switchLanguage } */
export const useSite = () => useContext(SiteContext);
